import { useEffect, useState } from 'react';
import type { EmailLabel, LiveEmail, LiveEmailBody } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { toast } from '../components/toast';
import { EmptyState, ErrorState, LockedNote, Notice, Skeleton } from '../components/ui';
import { chat, useChat } from '../lib/ai';
import { api } from '../lib/api';
import { fmt } from '../lib/format';
import { google, useGoogle } from '../lib/google';
import { usePrefs } from '../lib/prefs';
import { replaceHash } from '../lib/router';

/* E-mail real: Gmail somente leitura. Lista e triagem guardadas no Jarvis; o texto
   da mensagem é buscado na hora, já convertido em texto puro pelo servidor. */

const LABELS: Record<EmailLabel, { label: string; cls: string }> = {
  responder: { label: 'Responder', cls: 'badge-accent' },
  importante: { label: 'Importante', cls: 'badge-warn' },
  informativo: { label: 'Informativo', cls: 'badge-info' },
  newsletter: { label: 'Newsletter', cls: 'badge-off' }
};
type Filter = 'todos' | 'naolidos' | EmailLabel;
const STALE_MS = 5 * 60_000;
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado.');

function Reader({ m, account, onBack }: { m: LiveEmail | undefined; account: string; onBack: () => void }) {
  const { displayName } = usePrefs();
  const ai = useChat().status;
  const [body, setBody] = useState<LiveEmailBody | null>(null);
  const [bodyErr, setBodyErr] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [instructions, setInstructions] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBody(null); setBodyErr(null); setDraft(''); setInstructions('');
    if (!m) return;
    const ac = new AbortController();
    api.get<LiveEmailBody>(`/api/email/messages/${encodeURIComponent(m.id)}`, ac.signal)
      .then(setBody, (e: Error) => { if (!ac.signal.aborted) setBodyErr(e); });
    return () => ac.abort();
  }, [m?.id, attempt]);

  if (!m) return <EmptyState icon="mail" title="Selecione uma mensagem" text="O texto aparece aqui, sem imagens nem links clicáveis, com a classificação e o motivo." compact />;

  const prepare = async () => {
    setBusy(true);
    try {
      const q = displayName ? `?nome=${encodeURIComponent(displayName)}` : '';
      const r = await api.post<{ draft: string }>(`/api/email/messages/${encodeURIComponent(m.id)}/draft${q}`, { instructions: instructions.trim() || undefined });
      setDraft(r.draft);
      void chat.loadStatus();
    } catch (e) { toast(errMsg(e), 'alert'); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(draft); toast('Rascunho copiado. Cole no Gmail para enviar.', 'check'); }
    catch { toast('Não consegui copiar. Selecione o texto e use Ctrl+C.', 'alert'); }
  };
  const gmailUrl = `https://mail.google.com/mail/?authuser=${encodeURIComponent(account)}#all/${encodeURIComponent(m.threadId)}`;

  return (
    <div className="mail-reader">
      <button type="button" className="btn btn-sm btn-ghost back-btn" onClick={onBack}><Icon name="arrowLeft" />Voltar à caixa de entrada</button>
      <div className="chips">
        {m.label ? <span className={`badge ${LABELS[m.label].cls}`}>{LABELS[m.label].label}</span> : <span className="badge badge-off">Sem triagem</span>}
        {m.unread && <span className="badge badge-info">Não lido</span>}
      </div>
      <h2>{m.subject}</h2>
      <div className="mail-headers">
        <span><strong>{m.from.name}</strong>{m.from.address && m.from.address !== m.from.name && <> &lt;{m.from.address}&gt;</>}</span>
        <span>{fmt.day(m.receivedAt)}, {fmt.time(m.receivedAt)}</span>
      </div>
      {m.reason && <Notice icon="spark"><strong>Por que esta classificação:</strong> {m.reason}</Notice>}
      {!body && !bodyErr && <Skeleton kind="line" />}
      {bodyErr && <ErrorState compact error={bodyErr} onRetry={() => setAttempt((a) => a + 1)} />}
      {body && (
        <>
          <div className="mail-body">{body.text || '(mensagem sem texto)'}</div>
          {body.truncated && <p className="row-sub">Mensagem longa: mostrando o começo. Veja o resto no Gmail.</p>}
          {body.attachments.length > 0 && <p className="row-sub"><Icon name="layers" /> Anexos (não abertos pelo Jarvis): {body.attachments.join(', ')}</p>}
        </>
      )}
      <div className="chips"><a className="btn btn-sm btn-ghost" href={gmailUrl} target="_blank" rel="noopener noreferrer"><Icon name="external" />Abrir no Gmail</a></div>

      {ai?.configured ? (
        <section className="draft" aria-label="Rascunho de resposta">
          <div className="field">
            <label htmlFor="draft-instr">Orientações para a resposta (opcional)</label>
            <input id="draft-instr" className="input" maxLength={500} placeholder="Ex.: aceitar, mas propor sexta à tarde" value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </div>
          <div className="chips"><button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={prepare}><Icon name="edit" />{busy ? 'Preparando…' : draft ? 'Preparar outra versão' : 'Preparar resposta'}</button></div>
          {draft && (
            <>
              <div className="draft-body">{draft}</div>
              <div className="chips"><button type="button" className="btn btn-sm" onClick={copy}><Icon name="check" />Copiar rascunho</button></div>
            </>
          )}
        </section>
      ) : (
        <p className="row-sub">Rascunhos de resposta precisam do modelo de IA configurado.</p>
      )}
      <LockedNote>O Jarvis só lê o Gmail: não envia, não apaga e não marca como lido. Para responder, copie o rascunho e envie pelo Gmail.</LockedNote>
    </div>
  );
}

function Connect({ reconnect }: { reconnect?: boolean }) {
  return (
    <EmptyState icon="mail" title={reconnect ? 'Conecte o Gmail de novo' : 'Conectar o Gmail'}
      text={<>
        {reconnect ? 'O Google deixou de aceitar a autorização anterior (no modo de teste do Google, isso acontece a cada 7 dias). ' : ''}
        O Jarvis vai pedir ao Google só a permissão de <strong>ler</strong> seus e-mails: não envia, não apaga, não move nada.
        A autorização fica guardada criptografada, e você desconecta quando quiser, apagando tudo o que foi importado.
        Na tela do Google pode aparecer “O Google não verificou este app”: é esperado, porque o app é só seu. Clique em <strong>Avançado</strong> e em <strong>Acessar Jarvis Master</strong>.
      </>}
      actions={[{ label: reconnect ? 'Conectar de novo' : 'Conectar Gmail', icon: 'plug', primary: true, onClick: () => google.connect() }]} />
  );
}

export function EmailLive({ params }: { params: string[] }) {
  const gs = useGoogle();
  const ai = useChat().status;
  const [items, setItems] = useState<LiveEmail[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [triaging, setTriaging] = useState(false);
  const [filter, setFilter] = useState<Filter>('todos');
  const [selected, setSelected] = useState<string | null>(params[0] ?? null);

  useEffect(() => { void google.load(); void chat.loadStatus(); }, []);
  useEffect(() => { setSelected(params[0] ?? null); }, [params[0]]);

  const sync = async (quiet = false) => {
    setSyncing(true);
    try {
      const r = await api.post<{ items: LiveEmail[] }>('/api/email/sync');
      setItems(r.items); setError(null);
      await google.load();
      if (!quiet) toast('Caixa de entrada atualizada.', 'refresh');
    } catch (e) {
      if (!quiet) toast(errMsg(e), 'alert');
      await google.load();
      if (!items) setError(e as Error);
    } finally { setSyncing(false); }
  };

  useEffect(() => {
    if (!gs?.connected || gs.needsReconnect) return;
    api.get<{ items: LiveEmail[] }>('/api/email/messages').then((r) => {
      setItems(r.items);
      const stale = !gs.lastSyncAt || Date.now() - new Date(gs.lastSyncAt).getTime() > STALE_MS;
      if (stale) void sync(true);
    }, (e: Error) => setError(e));
  }, [gs?.connected, gs?.needsReconnect]);

  const classify = async () => {
    setTriaging(true);
    try {
      const r = await api.post<{ items: LiveEmail[]; classified: number }>('/api/email/triage');
      setItems(r.items);
      toast(r.classified ? `${r.classified} e-mails classificados.` : 'Nada novo para classificar.', 'spark');
      void chat.loadStatus();
    } catch (e) { toast(errMsg(e), 'alert'); } finally { setTriaging(false); }
  };

  if (!gs) return <Skeleton kind="list" count={6} />;
  if (!gs.configured) {
    return <EmptyState icon="mail" title="Gmail ainda não configurado no servidor"
      text="Faltam as credenciais do Google na Render (Parte F do guia de configuração): GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, GOOGLE_REDIRECT_URI e TOKEN_ENCRYPTION_KEY."
      actions={[{ label: 'Ver integrações', href: '#/configuracoes/integracoes', icon: 'plug' }]} />;
  }
  if (!gs.connected) return <Connect />;
  if (gs.needsReconnect) return <Connect reconnect />;
  if (error && !items) return <ErrorState error={error} onRetry={() => { setError(null); void sync(); }} />;
  if (!items) return <Skeleton kind="list" count={6} />;

  const select = (id: string | null) => {
    setSelected(id);
    replaceHash(id ? `#/email/${encodeURIComponent(id)}` : '#/email');
  };
  const current = items.find((m) => m.id === selected);
  const counts = (f: Filter) => items.filter((m) => f === 'todos' || (f === 'naolidos' ? m.unread : m.label === f)).length;
  const filters: [Filter, string][] = [['todos', 'Todos'], ['naolidos', 'Não lidos'], ...Object.entries(LABELS).map(([k, v]) => [k as EmailLabel, v.label] as [Filter, string])];
  const visible = items.filter((m) => filter === 'todos' || (filter === 'naolidos' ? m.unread : m.label === filter));
  const pending = items.filter((m) => !m.label).length;

  return (
    <>
      <div className="mail-toolbar">
        <span className="row-sub"><Icon name="lock" />{gs.email} · só leitura · {gs.lastSyncAt ? `atualizado ${fmt.relative(gs.lastSyncAt)}` : 'ainda não atualizado'}</span>
        <div className="chips">
          {ai?.configured && pending > 0 && (
            <button type="button" className="btn btn-sm btn-primary" disabled={triaging} onClick={classify}><Icon name="spark" />{triaging ? 'Classificando…' : `Classificar ${pending} com IA`}</button>
          )}
          <button type="button" className="btn btn-sm" disabled={syncing} onClick={() => void sync()}><Icon name="refresh" />{syncing ? 'Atualizando…' : 'Atualizar'}</button>
        </div>
      </div>
      <div className={`mail ${current ? 'reading' : ''}`}>
        <section className="card mail-list-card">
          <div className="chips mb-16" role="group" aria-label="Filtrar">
            {filters.map(([k, l]) => <button key={k} type="button" className="chip" aria-pressed={k === filter} onClick={() => setFilter(k)}>{l} · {counts(k)}</button>)}
          </div>
          <div className="mail-list">
            {visible.map((m) => (
              <button key={m.id} type="button" className={`mail-item ${m.unread ? 'unread' : ''}`} aria-current={m.id === selected} onClick={() => select(m.id)}>
                <span className="from"><span>{m.from.name}</span><span className="time">{fmt.smart(m.receivedAt)}</span></span>
                <span className="subject">{m.label && <i className={`mail-dot ${m.label}`} aria-label={LABELS[m.label].label} />}{m.subject}</span>
                <span className="snippet">{m.snippet}</span>
              </button>
            ))}
            {visible.length === 0 && <EmptyState icon="inbox" title={items.length ? 'Nada neste filtro' : 'Caixa de entrada vazia'} compact />}
          </div>
        </section>
        <section className="card mail-reader-card" aria-live="polite"><Reader m={current} account={gs.email ?? ''} onBack={() => select(null)} /></section>
      </div>
    </>
  );
}
